import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent } from './ui/dialog';
import { companyInfo } from '../mockData';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: 'residential',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Send to backend API
      const response = await axios.post(`${BACKEND_URL}/api/contact/quote`, formData);
      
      if (response.data.status === 'success') {
        setShowThankYou(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: 'residential',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error sending your request. Please try calling us directly at ' + companyInfo.phone);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section id="contact" className="py-24 pt-32 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
              Get In Touch
            </h2>
            <div className="flex items-center justify-center space-x-2 mb-6">
              <div className="h-1 w-12 bg-gold rounded-full"></div>
              <div className="h-2 w-2 bg-gold rounded-full"></div>
              <div className="h-1 w-20 bg-gold rounded-full"></div>
              <div className="h-2 w-2 bg-gold rounded-full"></div>
              <div className="h-1 w-12 bg-gold rounded-full"></div>
            </div>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Ready to power your project? Contact us for a free quote
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="border-2 border-gray-200 hover:border-gold/30 transition-colors">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center space-x-3 text-black">
                    <Phone className="w-6 h-6 text-gold" />
                    <span>Phone</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <a 
                    href={`tel:${companyInfo.phone}`}
                    className="text-lg text-gray-700 hover:text-gold transition-colors"
                  >
                    {companyInfo.phone}
                  </a>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-200 hover:border-gold/30 transition-colors">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center space-x-3 text-black">
                    <Mail className="w-6 h-6 text-gold" />
                    <span>Email</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <a 
                    href={`mailto:${companyInfo.email}`}
                    className="text-lg text-gray-700 hover:text-gold transition-colors break-all"
                  >
                    {companyInfo.email}
                  </a>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-200 hover:border-gold/30 transition-colors">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center space-x-3 text-black">
                    <MapPin className="w-6 h-6 text-gold" />
                    <span>Location</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-lg text-gray-700">
                    {companyInfo.location}<br />
                    {companyInfo.postalCode}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <Card className="lg:col-span-2 border-2 border-gray-200">
              <CardHeader>
                <CardTitle className="text-2xl text-black">Request a Free Quote</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-black">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="mt-2 border-gray-300 focus:border-gold"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-black">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-2 border-gray-300 focus:border-gold"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone" className="text-black">Phone *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="mt-2 border-gray-300 focus:border-gold"
                        placeholder="(250) 123-4567"
                      />
                    </div>
                    <div>
                      <Label htmlFor="service" className="text-black">Service Type *</Label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        required
                        className="mt-2 w-full h-10 px-3 rounded-md border border-gray-300 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold"
                      >
                        <option value="residential">Residential</option>
                        <option value="commercial">Commercial</option>
                        <option value="industrial">Industrial</option>
                        <option value="renewable">Renewable Energy</option>
                        <option value="emergency">Emergency Service</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-black">Project Details *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="mt-2 border-gray-300 focus:border-gold"
                      placeholder="Please describe your electrical needs..."
                    />
                  </div>

                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gold hover:bg-gold/90 text-black font-semibold text-lg py-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Send Quote Request
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Thank You Modal */}
      <Dialog open={showThankYou} onOpenChange={setShowThankYou}>
        <DialogContent className="sm:max-w-md bg-white border-2 border-gold">
          <div className="relative p-6">
            {/* Close button */}
            <button
              onClick={() => setShowThankYou(false)}
              className="absolute top-2 right-2 text-gray-400 hover:text-gold transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Content */}
            <div className="text-center space-y-6 pt-4">
              <div className="w-20 h-20 bg-gold/20 rounded-full flex items-center justify-center mx-auto">
                <Mail className="w-10 h-10 text-gold" />
              </div>
              
              <div className="space-y-3">
                <h3 className="text-2xl font-bold text-black">
                  Thank You for Your Inquiry!
                </h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Someone on our amazing team will respond shortly.
                </p>
              </div>

              <Button
                onClick={() => setShowThankYou(false)}
                className="bg-gold hover:bg-gold/90 text-black font-semibold px-8 py-3 rounded-lg"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Contact;
